import React, { useEffect, useRef, useState } from "react";

import { INSTAGRAM_POST_DELETED } from "@/mk/autogenerated/translations/InstagramEmbed.a702e2ffbaa2ced12df7485cf81937a2";

import styles from "./InstagramEmbed.mscss";

// Instagram replaces the blockquote with an <iframe> once it processes the
// embed. Removed/unavailable posts leave the iframe collapsed, so anything
// below this height is treated as a failed embed and swapped for a plain link.
const MIN_RENDERED_EMBED_HEIGHT = 100;
const EMBED_POLL_INTERVAL_MS = 1000;
const EMBED_RENDER_TIMEOUT_MS = 10000;

const InstagramEmbed: React.FC<{ instagramUrl: string }> = ({ instagramUrl }) => {
    const wrapperRef = useRef<HTMLDivElement>(null);
    const blockquoteRef = useRef<HTMLQuoteElement>(null);
    const [failed, setFailed] = useState(false);

    const onAddedToDOM = () => {
        if ((window as any)?.instgrm) {
            (window as any)?.instgrm?.Embeds?.process();
        } else if (!document.getElementById("instagramEmbedJs")) {
            const instagramEmbedJs: HTMLScriptElement = document.createElement("script");
            instagramEmbedJs.id = "instagramEmbedJs";
            instagramEmbedJs.type = "text/javascript";
            instagramEmbedJs.async = true;
            instagramEmbedJs.src = "https://www.instagram.com/embed.js";
            const firstScript = document.getElementsByTagName("script")[0];
            firstScript?.parentNode?.insertBefore(instagramEmbedJs, firstScript);

            // Wait for the script to load and then process the embeds
            instagramEmbedJs.onload = () => {
                (window as any)?.instgrm?.Embeds?.process();
            };
        }
    };

    useEffect(() => {
        if (!blockquoteRef.current) {
            return undefined;
        }

        let observer: MutationObserver | undefined;
        if (blockquoteRef.current.isConnected) {
            onAddedToDOM();
        } else {
            observer = new MutationObserver(() => {
                if (blockquoteRef.current?.isConnected) {
                    onAddedToDOM();
                    observer?.disconnect(); // Stop observing once the function is called
                }
            });
            observer.observe(document.body, { childList: true, subtree: true });
        }

        // Poll for the processed embed; once it has grown it rendered fine, and
        // if it is still collapsed by the deadline the post is unavailable.
        let elapsed = 0;
        const poll = window.setInterval(() => {
            elapsed += EMBED_POLL_INTERVAL_MS;
            const iframe = wrapperRef.current?.querySelector("iframe");
            const height = iframe?.getBoundingClientRect().height ?? 0;
            if (height >= MIN_RENDERED_EMBED_HEIGHT) {
                window.clearInterval(poll);
            } else if (elapsed >= EMBED_RENDER_TIMEOUT_MS) {
                window.clearInterval(poll);
                setFailed(true);
            }
        }, EMBED_POLL_INTERVAL_MS);

        return () => {
            observer?.disconnect();
            window.clearInterval(poll);
        };
    }, []);

    if (failed) {
        // Force an http(s) scheme so a crafted non-http url can never become a
        // javascript:/data: href once it is turned into a clickable link.
        const href = /^https?:\/\//i.test(instagramUrl) ? instagramUrl : `https://${instagramUrl}`;

        return (
            <a
                className={styles.InstagramEmbed__fallback}
                data-google-interstitial="false"
                href={href}
                rel="noopener nofollow"
                target="_blank"
            >
                {instagramUrl}{" "}
                <span className={styles.InstagramEmbed__fallbackNote}>
                    {INSTAGRAM_POST_DELETED}
                </span>
            </a>
        );
    }

    return (
        <div ref={wrapperRef} className={styles.InstagramEmbed}>
            <blockquote
                ref={blockquoteRef}
                className="instagram-media"
                data-instgrm-captioned
                data-instgrm-permalink={instagramUrl}
                data-instgrm-version="14"
            />
            <div className={styles.InstagramEmbed__mask} />
        </div>
    );
};

export default InstagramEmbed;
